import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Download, Share2, FileImage, Printer, CheckSquare, Square, Copy } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckboxItem = ({ checked, onChange, children }) => (
  <label className="flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50">
    <div onClick={onChange} className="cursor-pointer">
      {checked ? (
        <CheckSquare className="w-4 h-4 text-blue-600" />
      ) : (
        <Square className="w-4 h-4 text-gray-400" />
      )}
    </div>
    <span className="text-sm">{children}</span>
  </label>
);

const ReportPreview = ({ report, sections }) => {
  if (!report) return null;

  return (
    <div className="bg-white border border-gray-300 rounded-lg p-6 space-y-4 max-h-96 overflow-y-auto">
      <div className="text-center border-b pb-4 mb-4">
        <h2 className="text-xl font-bold text-gray-900">BURN ASSESSMENT REPORT</h2>
        <p className="text-sm text-gray-600 mt-1">Generated: {format(new Date(report.generated_at), 'PPP p')}</p>
        <p className="text-xs text-gray-500">Report ID: {report.report_id}</p>
      </div>

      {sections.patientInfo && (
        <div className="space-y-2">
          <h3 className="font-semibold text-gray-800 border-b pb-1">PATIENT INFORMATION</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="font-medium">Patient ID:</span> {report.session_info?.patient_id}</div>
            <div><span className="font-medium">Injury Date:</span> {report.session_info?.injury_date ? format(new Date(report.session_info.injury_date), 'PP') : 'N/A'}</div>
            <div><span className="font-medium">Burn Location:</span> {report.session_info?.burn_location}</div>
            <div><span className="font-medium">Burn Cause:</span> {report.session_info?.burn_cause?.replace('_', ' ')}</div>
          </div>
        </div>
      )}

      {sections.analysis && (
        <div className="space-y-2">
          <h3 className="font-semibold text-gray-800 border-b pb-1">3D ANALYSIS RESULTS</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="font-medium">Surface Area:</span> {report.clinical_metrics?.surface_area_cm2} cm²</div>
            <div><span className="font-medium">Volume:</span> {report.clinical_metrics?.volume_cm3} cm³</div>
            <div><span className="font-medium">Max Depth:</span> {report.clinical_metrics?.max_depth_mm} mm</div>
            <div><span className="font-medium">Avg Depth:</span> {report.clinical_metrics?.avg_depth_mm} mm</div>
            <div><span className="font-medium">TBSA:</span> {report.clinical_metrics?.tbsa_percentage}%</div>
            <div><span className="font-medium">Healing Stage:</span> {report.clinical_metrics?.healing_stage}</div>
          </div>
          <div className="mt-2">
            <span className="font-medium text-sm">Analysis Confidence:</span> 
            <span className="ml-2 text-sm">{report.clinical_metrics?.confidence}%</span>
          </div>
        </div>
      )}

      {sections.temporal && (
        <div className="space-y-2">
          <h3 className="font-semibold text-gray-800 border-b pb-1">TEMPORAL PROGRESSION</h3>
          <p className="text-sm text-gray-600">Healing progression tracking over multiple assessments.</p>
          <div className="text-sm">
            <div><span className="font-medium">Assessment Date:</span> {report.session_info?.analysis_date ? format(new Date(report.session_info.analysis_date), 'PP') : 'N/A'}</div>
            <div><span className="font-medium">Images Analyzed:</span> {report.technical_info?.images_analyzed}</div>
          </div>
        </div>
      )}

      {sections.recommendations && (
        <div className="space-y-2">
          <h3 className="font-semibold text-gray-800 border-b pb-1">TREATMENT RECOMMENDATIONS</h3>
          <ul className="list-disc list-inside space-y-1 text-sm">
            {report.recommendations?.map((rec, index) => (
              <li key={index}>{rec}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-2 pt-4 border-t">
        <h3 className="font-semibold text-gray-800">TECHNICAL INFORMATION</h3>
        <div className="text-xs text-gray-600 space-y-1">
          <div>Reconstruction Method: {report.technical_info?.reconstruction_method}</div>
          <div>Analysis Confidence: {report.technical_info?.analysis_confidence}</div>
          <div>Images Processed: {report.technical_info?.images_analyzed}</div>
        </div>
      </div>
    </div>
  );
};

const ReportView = ({ sessionData, analysisData, onComplete }) => {
  const [reportType, setReportType] = useState('comprehensive');
  const [sections, setSections] = useState({
    patientInfo: true,
    analysis: true,
    temporal: false,
    clinicalImages: false,
    recommendations: true
  });
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [generatedReport, setGeneratedReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const reportTypes = [
    { value: 'comprehensive', label: 'Comprehensive Analysis' },
    { value: 'progress', label: 'Progress Report' },
    { value: 'executive', label: 'Executive Summary' },
    { value: 'insurance', label: 'Insurance Documentation' }
  ];

  const handleSectionChange = (section) => {
    setSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const generateReport = async () => {
    if (!sessionData?.id) {
      alert('No session data available for report generation');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/sessions/${sessionData.id}/report`, {
        report_type: reportType,
        sections: Object.keys(sections).filter(key => sections[key]),
        additional_notes: additionalNotes,
        include_images: sections.clinicalImages
      });

      setGeneratedReport(response.data);
    } catch (error) {
      console.error('Failed to generate report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    if (!sessionData?.id) return;

    try {
      const response = await axios.get(
        `${API}/sessions/${sessionData.id}/download/report`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `burn_report_${sessionData.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      alert('PDF report downloaded successfully!');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      alert('Failed to export PDF. Please generate a report first.');
    }
  };

  const exportWord = async () => {
    if (!sessionData?.id) return;

    try {
      const response = await axios.get(
        `${API}/sessions/${sessionData.id}/download/report-word`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `burn_consultation_${sessionData.id}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      alert('Word document downloaded successfully!');
    } catch (error) {
      console.error('Failed to export Word document:', error);
      alert('Failed to export Word document. Please generate a report first.');
    }
  };

  const exportDICOM = () => {
    alert('DICOM export functionality requires specialized medical imaging libraries. This would integrate with hospital PACS systems in a production environment.');
  };

  const shareReport = () => {
    if (!generatedReport) {
      alert('Please generate a report first');
      return;
    }

    // Simulate sharing functionality
    navigator.clipboard.writeText(`Burn Assessment Report - Patient: ${sessionData?.patient_id}, Report ID: ${generatedReport.report_id}`)
      .then(() => alert('Report link copied to clipboard'))
      .catch(() => alert('Sharing functionality would integrate with hospital communication systems'));
  };

  const copyToClipboard = () => {
    if (!generatedReport) return;
    
    const reportText = `
BURN ASSESSMENT REPORT
Report ID: ${generatedReport.report_id}
Generated: ${format(new Date(generatedReport.generated_at), 'PPP p')}

PATIENT INFORMATION
Patient ID: ${generatedReport.session_info?.patient_id}
Burn Location: ${generatedReport.session_info?.burn_location}
Burn Cause: ${generatedReport.session_info?.burn_cause}

CLINICAL METRICS  
Surface Area: ${generatedReport.clinical_metrics?.surface_area_cm2} cm²
Volume: ${generatedReport.clinical_metrics?.volume_cm3} cm³
Max Depth: ${generatedReport.clinical_metrics?.max_depth_mm} mm
TBSA: ${generatedReport.clinical_metrics?.tbsa_percentage}%
Healing Stage: ${generatedReport.clinical_metrics?.healing_stage}
    `;
    
    navigator.clipboard.writeText(reportText)
      .then(() => alert('Report copied to clipboard'))
      .catch(() => alert('Failed to copy report'));
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-6 h-6 text-green-600" />
            Clinical Report Generation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600">
            Generate comprehensive clinical reports for burn assessment and documentation.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuration Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Report Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Report Type */}
            <div className="space-y-2">
              <Label>Report Type</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  {reportTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Include Sections */}
            <div className="space-y-3">
              <Label>Include Sections</Label>
              <div className="space-y-2 border rounded-lg p-3">
                <CheckboxItem 
                  checked={sections.patientInfo} 
                  onChange={() => handleSectionChange('patientInfo')}
                >
                  Patient Information
                </CheckboxItem>
                <CheckboxItem 
                  checked={sections.analysis} 
                  onChange={() => handleSectionChange('analysis')}
                >
                  3D Analysis Results
                </CheckboxItem>
                <CheckboxItem 
                  checked={sections.temporal} 
                  onChange={() => handleSectionChange('temporal')}
                >
                  Temporal Progression
                </CheckboxItem>
                <CheckboxItem 
                  checked={sections.clinicalImages} 
                  onChange={() => handleSectionChange('clinicalImages')}
                >
                  Clinical Images
                </CheckboxItem>
                <CheckboxItem 
                  checked={sections.recommendations} 
                  onChange={() => handleSectionChange('recommendations')}
                >
                  Treatment Recommendations
                </CheckboxItem>
              </div>
            </div>

            {/* Additional Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Clinical Notes</Label>
              <textarea
                id="notes"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter any additional clinical observations, treatment notes, or recommendations..."
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
              />
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button 
                onClick={generateReport} 
                disabled={loading}
                className="w-full"
                size="lg"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Generating Report...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Generate Report
                  </>
                )}
              </Button>

              <div className="grid grid-cols-3 gap-2">
                <Button 
                  variant="outline" 
                  onClick={exportPDF}
                  disabled={!generatedReport}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Export PDF
                </Button>
                <Button 
                  variant="outline" 
                  onClick={exportWord}
                  disabled={!generatedReport}
                >
                  <FileText className="w-4 h-4 mr-1" />
                  Export Word
                </Button>
                <Button 
                  variant="outline" 
                  onClick={copyToClipboard}
                  disabled={!generatedReport}
                >
                  <Copy className="w-4 h-4 mr-1" />
                  Copy Text
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button 
                  variant="outline" 
                  onClick={exportDICOM}
                  disabled={!generatedReport}
                >
                  <FileImage className="w-4 h-4 mr-1" />
                  Export DICOM
                </Button>
                <Button 
                  variant="outline" 
                  onClick={shareReport}
                  disabled={!generatedReport}
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  Share Report
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Report Preview</CardTitle>
          </CardHeader>
          <CardContent>
            {generatedReport ? (
              <ReportPreview report={generatedReport} sections={sections} />
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-2">Report Preview</p>
                <p className="text-sm text-gray-500">Generate a report to see the preview here</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Session Summary */}
      {sessionData && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Session Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-600">Session ID:</span>
                <p className="font-mono">{sessionData.id}</p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Patient:</span>
                <p>{sessionData.patient_id}</p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Location:</span>
                <p>{sessionData.burn_location}</p>
              </div>
              <div>
                <span className="font-medium text-gray-600">Confidence:</span>
                <p>{analysisData?.metrics?.confidence}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Complete Button */}
      <Card>
        <CardContent className="flex justify-between items-center p-6">
          <div>
            <h4 className="font-medium mb-1">Report Generation Complete</h4>
            <p className="text-sm text-gray-600">
              {generatedReport ? 'Report generated successfully' : 'Ready to generate clinical reports'}
            </p>
          </div>
          <Button onClick={() => onComplete && onComplete()}>
            Complete Analysis
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportView;